import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, ToggleRight, Layers } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

const money = (v) => `$${(v || 0).toLocaleString('en-NZ', { maximumFractionDigits: 0 })}`;
const Row = ({ label, oldV, newV, testId }) => (
  <tr className="border-t border-[#F1EFEC]" data-testid={testId}>
    <td className="px-4 py-2 text-slate-700">{label}</td><td className="font-mono text-slate-500">{oldV}</td><td className="font-mono font-semibold">{newV}</td>
  </tr>
);

export default function JobModelAdminPage() {
  const { api } = useAuth();
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get('/job-model/dry-run').then((r) => setD(r.data)).catch(() => toast.error('Could not load dry-run')), [api]);
  useEffect(() => { load(); }, [load]);

  const toggle = async () => {
    setBusy(true);
    try {
      await api.post('/job-model/enable', { enabled: !d.enabled });
      toast.success(d.enabled ? 'Switched back to the old counting' : 'Job + Quotes model switched on');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  if (!d) return <div className="flex items-center gap-2 text-slate-500 p-8" data-testid="job-model-loading"><Loader2 className="animate-spin" size={16} /> Running dry-run…</div>;
  return (
    <div className="space-y-4 max-w-4xl" data-testid="job-model-admin-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 font-[Manrope] flex items-center gap-2"><Layers size={26} /> Job + Quotes model</h1>
          <p className="text-sm text-slate-500 mt-1 max-w-2xl">Jobs (the first appraisal) are counted once; their quotes carry the money. Alternative variants count once. Review the numbers, then switch on — you can switch back any time.</p>
        </div>
        <button data-testid="job-model-toggle-btn" onClick={toggle} disabled={busy}
          className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-md text-white transition-colors ${d.enabled ? 'bg-slate-600 hover:bg-slate-700' : 'bg-[#0F4F1B] hover:bg-[#0a3c14]'}`}>
          {busy ? <Loader2 size={14} className="animate-spin" /> : <ToggleRight size={14} />} {d.enabled ? 'Switch off' : 'Switch on'}
        </button>
      </div>
      <div data-testid="job-model-status" className={`text-xs px-3 py-2 rounded border ${d.enabled ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-amber-50 border-amber-200 text-amber-800'}`}>
        {d.enabled ? 'ON — reports count jobs and quotes separately.' : 'OFF — dry-run only, reports still use the old counting.'}
      </div>
      <div className="bg-white border border-[#E2E2DF] rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 text-left">
            <tr><th className="px-4 py-2">Measure</th><th>Old</th><th>New</th></tr>
          </thead>
          <tbody>
            <Row testId="dry-run-count" label="Appraisals → Jobs / Quotes" oldV={d.appraisals_old} newV={`${d.jobs_new} jobs · ${d.quotes_new} quotes`} />
            <Row testId="dry-run-builds" label="Jobs that have quote builds" oldV="—" newV={d.jobs_with_builds} />
            {d.status_counts.map((s) => (
              <Row key={s.status} testId={`dry-run-status-${s.status.replace(/\s+/g, '-').toLowerCase()}`} label={`In ${s.status}`} oldV={s.old} newV={`${s.jobs} jobs · ${s.quotes} quotes`} />
            ))}
            <Row testId="dry-run-contract" label="Contract value total" oldV={money(d.contract_total_old)} newV={money(d.contract_total_new)} />
            <Row testId="dry-run-parent-money" label="Money sitting on parent jobs (now excluded)" oldV="counted" newV={money(d.parent_money_excluded)} />
            <Row testId="dry-run-variants" label="Runner variants to group as alternatives" oldV="counted separately" newV={d.runner_variants_to_group} />
          </tbody>
        </table>
      </div>
      <p className="text-[11px] text-slate-400">{d.note}</p>
    </div>
  );
}
